import { Injectable } from '@angular/core';
import {
  CLIENT_ALL_ROLES,
  CLIENT_EDIT_ENDPOINTS_ROLES,
  CLIENT_EDIT_PRODUCTION_ENDPOINTS,
  CLIENT_EDIT_PRODUCTION_ENDPOINTS_ROLES,
  CLIENT_EDIT_PRODUCTION_ROUTES_ROLES,
  CLIENT_EDIT_PRODUCTION_SECRETS_ROLES,
  CLIENT_EDIT_ROUTES_ROLES,
  CLIENT_EDIT_SECRETS_ROLES,
  CLIENT_EDIT_FLOWS_ROLES,
  CLIENT_EDIT_MENU_TEXT_ONLY_ROLES,
  CLIENT_VIEW_ONLY,
  CLIENT_REVIEWER_ROLES,
  CLIENT_PRODUCTION_DEPLOY_ROLES,
  CLIENT_EDIT_PRODUCTION_KEYWORDS_ROLES,
  CLIENT_MANAGE_ASSETS_ROLES,
  INTERNAL_ALL_ROLES,
  INTERNAL_EDIT_ENDPOINTS_ROLES,
  INTERNAL_EDIT_FLOWS_ROLES,
  INTERNAL_EDIT_MENU_TEXT_ONLY_ROLES,
  INTERNAL_EDIT_PRODUCTION_ENDPOINTS_ROLES,
  INTERNAL_EDIT_PRODUCTION_ROUTES_ROLES,
  INTERNAL_EDIT_PRODUCTION_SECRETS_ROLES,
  INTERNAL_EDIT_ROUTES_ROLES,
  INTERNAL_EDIT_SECRETS_ROLES,
  INTERNAL_VIEW_ONLY,
  INTERNAL_REVIEWER_ROLES,
  INTERNAL_PRODUCTION_DEPLOY_ROLES,
  INTERNAL_EDIT_PRODUCTION_KEYWORDS_ROLES,
  INTERNAL_EDIT_KEYWORDS_ROLES,
  INTERNAL_MANAGE_ASSETS_ROLES,
  CLIENT_EDIT_KEYWORDS_ROLES,
  INTERNAL_ADMIN, CLIENT_ADMIN,
} from './roles';
import { SharedStorageService } from 'ngx-store';
import { SharedService } from '../store/shared-store/shared-storage.service';

@Injectable()
export class RolesService {
  constructor(
    private sharedStorageService: SharedStorageService,
    private sharedService: SharedService
  ) {}
  public isUserInRoles(roles: Array<string>): boolean {
    const storedUserInfo = this.sharedStorageService.get('userInfo');
    const map = storedUserInfo['permissions'];
    for (const role of roles) {
      if (map.has(role)) {
        return true;
      }
    }
    return false;
  }

  static isUserRoles(userRoles: {}, roles: Array<string>): boolean {
    for (const role of roles) {
      if (userRoles.hasOwnProperty(role)) {
        return true;
      }
    }
    return false;
  }

  public isAdmin(role: string):boolean {
    const storedUserInfo = this.sharedStorageService.get('userInfo');
    const map = storedUserInfo['permissions'];
    return map.has(role) ? true : false;
  }

  public isClientAdmin() {
    return this.isAdmin(CLIENT_ADMIN);
  }

  public isUserInternal(): boolean {
    return this.isUserInRoles(INTERNAL_ALL_ROLES);
  }

  public isInternalAdmin(): boolean {
    return this.isAdmin(INTERNAL_ADMIN);
  }

  public isUserInternalAdmin(): boolean {
    return this.isUserInRoles([INTERNAL_ADMIN]);
  }

  public isUserClient(): boolean {
    return this.isUserInRoles(CLIENT_ALL_ROLES);
  }

  public canEditEndpoints(env: string): boolean {
    if (env === 'production' || env === 'preprod' || env === 'prod') {
      const ALL_PROD_EDIT_ENDPOINT_ROLES: Array<string> =
        INTERNAL_EDIT_PRODUCTION_ENDPOINTS_ROLES.concat(CLIENT_EDIT_PRODUCTION_ENDPOINTS_ROLES);
      return this.isUserInRoles(ALL_PROD_EDIT_ENDPOINT_ROLES);
    } else {
      const ALL_EDIT_ENDPOINT_ROLES: Array<string> = INTERNAL_EDIT_ENDPOINTS_ROLES.concat(
        CLIENT_EDIT_ENDPOINTS_ROLES
      );
      return this.isUserInRoles(ALL_EDIT_ENDPOINT_ROLES);
    }
  }

  public canEditRoutes(env: string): boolean {
    if (env === 'production' || env === 'preprod' || env === 'prod') {
      const ALL_PROD_EDIT_ROUTES_ROLES: Array<string> =
        INTERNAL_EDIT_PRODUCTION_ROUTES_ROLES.concat(CLIENT_EDIT_PRODUCTION_ROUTES_ROLES);
      return this.isUserInRoles(ALL_PROD_EDIT_ROUTES_ROLES);
    } else {
      const ALL_EDIT_ROUTES_ROLES: Array<string> =
        INTERNAL_EDIT_ROUTES_ROLES.concat(CLIENT_EDIT_ROUTES_ROLES);
      return this.isUserInRoles(ALL_EDIT_ROUTES_ROLES);
    }
  }

  public canEditSecrets(env: string): boolean {
    if (env === 'production' || env === 'preprod' || env === 'prod') {
      const ALL_PROD_EDIT_SECRETS_ROLES: Array<string> =
        INTERNAL_EDIT_PRODUCTION_SECRETS_ROLES.concat(CLIENT_EDIT_PRODUCTION_SECRETS_ROLES);
      return this.isUserInRoles(ALL_PROD_EDIT_SECRETS_ROLES);
    } else {
      const ALL_EDIT_SECRETS_ROLES: Array<string> =
        INTERNAL_EDIT_SECRETS_ROLES.concat(CLIENT_EDIT_SECRETS_ROLES);
      return this.isUserInRoles(ALL_EDIT_SECRETS_ROLES);
    }
  }

  public canEditFlows(): boolean {
    const env = this.sharedService.getEnvironment();
    if (env === 'production' || env === 'preprod' || env === 'prod') {
      return false;
    } else {
      const ALL_EDIT_FLOW_ROLES = INTERNAL_EDIT_FLOWS_ROLES.concat(CLIENT_EDIT_FLOWS_ROLES);
      return this.isUserInRoles(ALL_EDIT_FLOW_ROLES);
    }
  }

  public canEditMenuTextOnly(): boolean {
    const ALL_EDIT_MENU_TEXT_ONLY_ROLES = INTERNAL_EDIT_MENU_TEXT_ONLY_ROLES.concat(
      CLIENT_EDIT_MENU_TEXT_ONLY_ROLES
    );
    return this.isUserInRoles(ALL_EDIT_MENU_TEXT_ONLY_ROLES);
  }

  public canViewOnly() {
    return this.isUserInRoles([INTERNAL_VIEW_ONLY]);
  }

  public isReviewer() {
    const ALL_REVIEWERS_ROLLES = INTERNAL_REVIEWER_ROLES.concat(CLIENT_REVIEWER_ROLES);
    return this.isUserInRoles(ALL_REVIEWERS_ROLLES);
  }

  public canDeploy(): boolean {
    const ALL_DEPLOYER_ROLES = INTERNAL_PRODUCTION_DEPLOY_ROLES.concat(
      CLIENT_PRODUCTION_DEPLOY_ROLES
    );
    return this.isUserInRoles(ALL_DEPLOYER_ROLES);
  }

  public canEditProductionEndpoints(): boolean {
    const ALL_PROD_ENDPOINT_ROLES = INTERNAL_EDIT_PRODUCTION_ENDPOINTS_ROLES.concat(
      CLIENT_EDIT_PRODUCTION_ENDPOINTS_ROLES
    );
    return this.isUserInRoles(ALL_PROD_ENDPOINT_ROLES);
  }

  public canEditKeywords(env: string): boolean {
    if (env === 'production' || env === 'preprod' || env === 'prod') {
      const ALL_PROD_EDIT_KEYWORDS_ROLES: Array<string> =
        INTERNAL_EDIT_PRODUCTION_KEYWORDS_ROLES.concat(CLIENT_EDIT_PRODUCTION_KEYWORDS_ROLES);
      return this.isUserInRoles(ALL_PROD_EDIT_KEYWORDS_ROLES);
    } else {
      const ALL_EDIT_KEYWORDS_ROLES: Array<string> = INTERNAL_EDIT_KEYWORDS_ROLES.concat(
        CLIENT_EDIT_KEYWORDS_ROLES
      );
      return this.isUserInRoles(ALL_EDIT_KEYWORDS_ROLES);
    }
  }

  public canEditAssets(): boolean {
    const ALL_MANAGE_ASSETS_ROLES = INTERNAL_MANAGE_ASSETS_ROLES.concat(CLIENT_MANAGE_ASSETS_ROLES);
    return this.isUserInRoles(ALL_MANAGE_ASSETS_ROLES);
  }
}
